import { createFileRoute } from "@tanstack/react-router";
import { TableLoaderRow } from "@/components/ui/loader";
import { useMemo, useState } from "react";
import { format } from "date-fns";
import { RouteGuard } from "@/components/auth/RouteGuard";
import { useAuditLogs, useAuditActors, useResolveAuditUuids, type AuditLogRow } from "@/features/audit-logs/api";
import { fmtDateTime } from "@/lib/utils";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { useIsReadOnly } from "@/hooks/useIsReadOnly";

const AUDITED_TABLES = [
  "work_orders",
  "transfers",
  "condemnations",
  "user_roles",
  "insurance_claims",
];
const ACTIONS = ["INSERT", "UPDATE", "DELETE"];
const PAGE_SIZE = 50;

export const Route = createFileRoute("/_authenticated/audit-logs")({
  component: () => (
    <RouteGuard module="audit_logs">
      <AuditLogsPage />
    </RouteGuard>
  ),
});

function summarize(row: AuditLogRow): string {
  const nd = (row.new_data ?? {}) as Record<string, unknown>;
  if (nd._source === "app" && typeof nd.message === "string") return nd.message;
  return `${row.table_name} ${row.action}`;
}

function extractUuids(obj: any): string[] {
  if (!obj || typeof obj !== "object") return [];
  const uuids = new Set<string>();
  const regex = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;
  for (const v of Object.values(obj)) {
    if (typeof v === "string" && regex.test(v)) {
      uuids.add(v);
    }
  }
  return Array.from(uuids);
}

function JsonDiffViewer({ oldData, newData }: { oldData: any; newData: any }) {
  const oldObj = (typeof oldData === 'object' && oldData) ? oldData as Record<string, any> : {};
  const newObj = (typeof newData === 'object' && newData) ? newData as Record<string, any> : {};
  const allKeys = Array.from(new Set([...Object.keys(oldObj), ...Object.keys(newObj)])).sort();

  const allUuids = useMemo(() => Array.from(new Set([...extractUuids(oldObj), ...extractUuids(newObj)])), [oldData, newData]);
  const resolved = useResolveAuditUuids(allUuids);
  const resolvedMap = resolved.data || {};

  if (allKeys.length === 0) return <div className="text-sm text-muted-foreground">No data recorded.</div>;

  return (
    <div className="rounded-md border overflow-hidden">
      <Table>
        <TableHeader>
          <TableRow className="bg-muted/50">
            <TableHead className="w-1/3">Field</TableHead>
            <TableHead className="w-1/3 border-l">Before</TableHead>
            <TableHead className="w-1/3 border-l">After</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {allKeys.map(key => {
            const oldVal = oldObj[key];
            const newVal = newObj[key];
            const isChanged = JSON.stringify(oldVal) !== JSON.stringify(newVal);
            
            const renderVal = (v: any) => {
              if (v === undefined || v === null) return <span className="text-muted-foreground">—</span>;
              if (typeof v === "object") return <pre className="text-[10px] m-0 whitespace-pre-wrap">{JSON.stringify(v, null, 2)}</pre>;
              if (typeof v === "string" && resolvedMap[v]) return <span className="font-semibold text-primary">{resolvedMap[v]}</span>;
              return String(v);
            };

            return (
              <TableRow key={key} className={isChanged ? "bg-amber-50/50 dark:bg-amber-950/20" : ""}>
                <TableCell className="font-mono text-xs font-medium">{key}</TableCell>
                <TableCell className="border-l font-mono text-xs break-all text-muted-foreground">{renderVal(oldVal)}</TableCell>
                <TableCell className="border-l font-mono text-xs break-all">{renderVal(newVal)}</TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}

function AuditLogsPage() {
  const readOnly = useIsReadOnly("audit_logs");
  const [tableName, setTableName] = useState<string>("all");
  const [action, setAction] = useState<string>("all");
  const [recordId, setRecordId] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [page, setPage] = useState(1);
  const [openRow, setOpenRow] = useState<AuditLogRow | null>(null);

  const filters = useMemo(
    () => ({
      table_name: tableName === "all" ? null : tableName,
      action: action === "all" ? null : action,
      record_id: recordId.trim() || null,
      from: from ? new Date(from).toISOString() : null,
      to: to ? new Date(`${to}T23:59:59`).toISOString() : null,
      page,
      pageSize: PAGE_SIZE,
    }),
    [tableName, action, recordId, from, to, page],
  );

  const { data, isLoading, isError, error } = useAuditLogs(filters);
  const rows = data?.rows ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const actorIds = useMemo(
    () => Array.from(new Set(rows.map((r) => r.actor_id).filter((x): x is string => !!x))),
    [rows],
  );
  const actors = useAuditActors(actorIds);
  const actorLabel = (id: string | null) => {
    if (!id) return "System / Unknown";
    const a = actors.data?.[id];
    return a?.full_name || a?.email || id.slice(0, 8);
  };

  const resetFilters = () => {
    setTableName("all");
    setAction("all");
    setRecordId("");
    setFrom("");
    setTo("");
    setPage(1);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Audit Logs</h1>
          <p className="text-sm text-muted-foreground">
            Privileged changes to work orders, transfers, condemnations, user roles, and insurance claims.
          </p>
        </div>
        {readOnly && <Badge variant="outline">Read-only</Badge>}
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Filters</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-3 sm:grid-cols-2 lg:grid-cols-6">
          <div className="space-y-1">
            <Label>Table</Label>
            <Select value={tableName} onValueChange={(v) => { setTableName(v); setPage(1); }}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All tables</SelectItem>
                {AUDITED_TABLES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label>Action</Label>
            <Select value={action} onValueChange={(v) => { setAction(v); setPage(1); }}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All actions</SelectItem>
                {ACTIONS.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label>Record ID</Label>
            <Input value={recordId} onChange={(e) => { setRecordId(e.target.value); setPage(1); }} placeholder="contains…" />
          </div>
          <div className="space-y-1">
            <Label>From</Label>
            <Input type="date" value={from} onChange={(e) => { setFrom(e.target.value); setPage(1); }} />
          </div>
          <div className="space-y-1">
            <Label>To</Label>
            <Input type="date" value={to} onChange={(e) => { setTo(e.target.value); setPage(1); }} />
          </div>
          <div className="flex items-end">
            <Button variant="outline" onClick={resetFilters} className="w-full">Reset</Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-44">Date/Time</TableHead>
                <TableHead>Actor</TableHead>
                <TableHead>Table</TableHead>
                <TableHead>Record</TableHead>
                <TableHead>Action</TableHead>
                <TableHead>Summary</TableHead>
                <TableHead className="w-20 text-right">Details</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading && <TableLoaderRow colSpan={7} />}
              {isError && (
                <TableRow><TableCell colSpan={7} className="text-center text-sm text-destructive py-8">{(error as Error)?.message ?? "Failed to load"}</TableCell></TableRow>
              )}
              {!isLoading && rows.length === 0 && (
                <TableRow><TableCell colSpan={7} className="text-center text-sm text-muted-foreground py-8">No audit entries match these filters.</TableCell></TableRow>
              )}
              {rows.map((r) => (
                <TableRow key={r.id}>
                  <TableCell className="font-mono text-xs">{format(new Date(r.created_at), "yyyy-MM-dd HH:mm:ss")}</TableCell>
                  <TableCell>{actorLabel(r.actor_id)}</TableCell>
                  <TableCell><Badge variant="secondary">{r.table_name}</Badge></TableCell>
                  <TableCell className="font-mono text-xs">{r.record_id?.slice(0, 8) ?? "—"}</TableCell>
                  <TableCell><Badge variant={r.action === "DELETE" ? "destructive" : r.action === "INSERT" ? "default" : "outline"}>{r.action}</Badge></TableCell>
                  <TableCell className="max-w-[36ch] truncate">{summarize(r)}</TableCell>
                  <TableCell className="text-right">
                    <Button size="sm" variant="ghost" onClick={() => setOpenRow(r)}>View</Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <div className="flex items-center justify-between text-sm text-muted-foreground">
        <div>{total.toLocaleString()} entries — page {page} of {totalPages}</div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>Previous</Button>
          <Button size="sm" variant="outline" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>Next</Button>
        </div>
      </div>

      <Sheet open={!!openRow} onOpenChange={(o) => !o && setOpenRow(null)}>
        <SheetContent side="right" className="w-full sm:max-w-2xl overflow-y-auto">
          {openRow && (
            <>
              <SheetHeader>
                <SheetTitle>{openRow.table_name} · {openRow.action}</SheetTitle>
                <SheetDescription>
                  {fmtDateTime(openRow.created_at)} · by {actorLabel(openRow.actor_id)}
                </SheetDescription>
              </SheetHeader>
              <div className="mt-4 space-y-4 text-sm">
                <div>
                  <div className="text-xs uppercase text-muted-foreground mb-1">Record ID</div>
                  <div className="font-mono break-all">{openRow.record_id ?? "—"}</div>
                </div>
                {(() => {
                  const nd = (openRow.new_data ?? {}) as Record<string, unknown>;
                  if (nd._source === "app") {
                    return (
                      <div className="rounded-md border bg-muted/40 p-3">
                        <div className="text-xs uppercase text-muted-foreground mb-1">Semantic event</div>
                        <div className="text-sm">{String(nd.message ?? "")}</div>
                        {nd.metadata !== undefined && nd.metadata !== null && (
                          <pre className="mt-2 overflow-auto text-xs">{JSON.stringify(nd.metadata as unknown, null, 2)}</pre>
                        )}
                      </div>
                    );
                  }
                  return <JsonDiffViewer oldData={openRow.old_data} newData={openRow.new_data} />;
                })()}
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
